import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DigitalCardsService, DigitalCardRequest, DigitalCardEmailRequest, DigitalCardResponse, DigitalCardTemplateResponse, Elements } from '@src/app/shared/backend/clients';
import { firstValueFrom } from 'rxjs';
import { SessionService } from '@src/app/shared/services/session.service';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { emailRegexValidator } from '@src/app/shared/validators/email.validator';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';

type TextElementKey = 'productName' | 'sku' | 'serialNumber' | 'warrantyCode' | 'purchaseDate' | 'retailerName';

type DigitalCardTemplateConfig = {
  background: {
    type: 'color' | 'image';
    color: string;
    imageDataUrl: string | null;
  };
  card: {
    width: number;
    height: number;
    borderRadius: number;
  };
  elements: {
    productName?: DigitalCardTextElement;
    sku?: DigitalCardTextElement;
    serialNumber?: DigitalCardTextElement;
    warrantyCode?: DigitalCardTextElement;
    purchaseDate?: DigitalCardTextElement;
    retailerName?: DigitalCardTextElement;
    qrCode?: DigitalCardQrElement;
  };
};

type DigitalCardTextElement = {
  active: boolean;
  xPercent: number;
  yPercent: number;
  color: string;
  fontSize: number;
  textAlign: 'left' | 'center' | 'right';
};

type DigitalCardQrElement = {
  active: boolean;
  xPercent: number;
  yPercent: number;
  size: number;
};

@Component({
    selector: 'app-view-digital-card',
    templateUrl: './view-digital-card.component.html',
    styleUrls: ['./view-digital-card.component.scss', './digital-card-print.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'app-view-digital-card'
    },
    standalone: false
})
export class ViewDigitalCardComponent implements OnInit, AfterViewInit, OnDestroy {

  emailCardIsEnabled: boolean = true;

  digitalCardTemplateIsMissing: boolean = false;
  digitalCardTemplateHasError: boolean = false;
  qrUrlIsMissing: boolean = false;

  @ViewChild('digitalCardPreviewWrapper') digitalCardPreviewWrapper?: ElementRef<HTMLDivElement>;

  readonlyCardScale = 1;
  readonlyCardResizeObserver?: ResizeObserver;

  itemId: number = 0;
  digitalCardId: number = 0;
  digitalCard: DigitalCardResponse = {};
  companyName: any = '';
  qrData: string = '';
  isFetchingQrData: boolean = true;
  displayEmailModalbox:boolean = false;
  allowViewProduct: boolean = false;
  showSecurityPasscode: boolean = false;

  textElementKeys: TextElementKey[] = [
    'productName',
    'sku',
    'serialNumber',
    'warrantyCode',
    'purchaseDate',
    'retailerName'
  ];

  digitalCardConfig: DigitalCardTemplateConfig = this.createDefaultDigitalCardConfig();

  public emailForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, emailRegexValidator(), Validators.maxLength(50)]],
  });

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    public translate: TranslateService,
    private toasterService: ToasterService,
    private session: SessionService,
    private digitalCardsService: DigitalCardsService,
    private permissionService: PermissionService,
    private zone: NgZone) { }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.digitalCardId = Number(this.route.snapshot.paramMap.get('digitalCardId'));
    this.itemId = this.digitalCardId;

    await this.getDigitalCardData();

    if (!this.digitalCard.brandId) {
      this.digitalCardTemplateHasError = true;
      console.error('Brand ID is missing from the digital card response.');
      return;
    }

    await this.getDigitalCardTemplate(this.digitalCard.brandId);

    this.allowViewProduct = await this.permissionService.hasActionPermissions(
      Actions.view_products
    );
  }

  ngAfterViewInit(): void {
    this.setupReadonlyCardResizeObserver();
  }

  private isNotFoundError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 404;
  }

  get canDisplayDigitalCardPreview(): boolean {
    return !this.digitalCardTemplateIsMissing && !this.digitalCardTemplateHasError;
  }

  async getDigitalCardData(): Promise<void> {
    try {
      const company = await this.session.getCompanyData();
      this.companyName = company?.name ?? '';

      this.digitalCard = await firstValueFrom(this.digitalCardsService.apiDigitalCardsIdGet(this.digitalCardId));

      if (this.digitalCard.id) {
        await this.generateQrCode(this.digitalCard.id);
      } else {
        console.error('Digital card ID is missing.');
      }
    } catch (error) {
      console.error('Error fetching digital card data:', error);
    }
  }

  async getDigitalCardTemplate(brandId: number): Promise<void> {
    try {
      const response = await firstValueFrom(this.digitalCardsService.apiDigitalCardsTemplateBrandIdGet(brandId));

      this.digitalCardTemplateIsMissing = false;
      this.digitalCardTemplateHasError = false;
      this.digitalCardConfig = this.createDigitalCardConfigFromTemplate(response);

      setTimeout(() => this.updateReadonlyCardScale());
    } catch (error) {
      if (this.isNotFoundError(error)) {
        this.digitalCardTemplateIsMissing = true;
        this.digitalCardTemplateHasError = false;
        return;
      }

      this.digitalCardTemplateIsMissing = false;
      this.digitalCardTemplateHasError = true;
      console.error('Error fetching digital card template:', error);
    }
  }

  private createDigitalCardConfigFromTemplate(response: DigitalCardTemplateResponse): DigitalCardTemplateConfig {
    const defaultConfig = this.createDefaultDigitalCardConfig();

    if (!response?.configuration) {
      return defaultConfig;
    }

    const configuration = response.configuration;

    return {
      background: {
        type: this.getBackgroundType(configuration.background?.type),
        color: configuration.background?.color || defaultConfig.background.color,
        imageDataUrl: this.toPngDataUrl(response.image)
      },
      card: {
        width: configuration.card?.width || defaultConfig.card.width,
        height: configuration.card?.height || defaultConfig.card.height,
        borderRadius: configuration.card?.borderRadius ?? defaultConfig.card.borderRadius
      },
      elements: this.createElementsFromTemplate(configuration.elements, defaultConfig.elements)
    };
  }

  private createElementsFromTemplate(
    savedElements: Elements | undefined,
    defaultElements: DigitalCardTemplateConfig['elements']
  ): DigitalCardTemplateConfig['elements'] {
    const elements: DigitalCardTemplateConfig['elements'] = {
      productName: this.createInactiveTextElement(defaultElements.productName!),
      sku: this.createInactiveTextElement(defaultElements.sku!),
      serialNumber: this.createInactiveTextElement(defaultElements.serialNumber!),
      warrantyCode: this.createInactiveTextElement(defaultElements.warrantyCode!),
      purchaseDate: this.createInactiveTextElement(defaultElements.purchaseDate!),
      retailerName: this.createInactiveTextElement(defaultElements.retailerName!),
      qrCode: {
        ...defaultElements.qrCode!,
        active: false
      }
    };

    this.textElementKeys.forEach((key) => {
      const savedElement = savedElements?.[key];
      const defaultElement = defaultElements[key];

      if (!savedElement || !defaultElement) {
        return;
      }

      elements[key] = {
        active: savedElement.active ?? true,
        xPercent: savedElement.xPercent ?? defaultElement.xPercent,
        yPercent: savedElement.yPercent ?? defaultElement.yPercent,
        color: savedElement.color || defaultElement.color,
        fontSize: savedElement.fontSize ?? defaultElement.fontSize,
        textAlign: this.getTextAlign(savedElement.textAlign)
      };
    });

    const savedQrCode = savedElements?.qrCode;

    if (savedQrCode) {
      elements.qrCode = {
        active: savedQrCode.active ?? true,
        xPercent: savedQrCode.xPercent ?? defaultElements.qrCode!.xPercent,
        yPercent: savedQrCode.yPercent ?? defaultElements.qrCode!.yPercent,
        size: savedQrCode.size ?? defaultElements.qrCode!.size
      };
    }

    return elements;
  }

  private createInactiveTextElement(element: DigitalCardTextElement): DigitalCardTextElement {
    return { ...element, active: false };
  }

  private createDefaultDigitalCardConfig(): DigitalCardTemplateConfig {
    return {
      background: {
        type: 'color',
        color: '#f3f4f6',
        imageDataUrl: null
      },
      card: {
        width: 640,
        height: 360,
        borderRadius: 16
      },
      elements: {
        productName: this.createTextElement(false, 6, 16),
        sku: this.createTextElement(false, 40, 19),
        serialNumber: this.createTextElement(true, 6, 29),
        warrantyCode: this.createTextElement(false, 67, 55),
        purchaseDate: this.createTextElement(false, 6, 79),
        retailerName: this.createTextElement(false, 6, 89),
        qrCode: {
          active: true,
          xPercent: 69,
          yPercent: 16,
          size: 128
        }
      }
    };
  }

  private createTextElement(
    active: boolean,
    xPercent: number,
    yPercent: number
  ): DigitalCardTextElement {
    return {
      active,
      xPercent,
      yPercent,
      color: '#111827',
      fontSize: 18,
      textAlign: 'left'
    };
  }

  private getBackgroundType(type: string | null | undefined): 'color' | 'image' {
    return type === 'image' ? 'image' : 'color';
  }

  private getTextAlign(textAlign: string | null | undefined): 'left' | 'center' | 'right' {
    if (textAlign === 'center' || textAlign === 'right') {
      return textAlign;
    }

    return 'left';
  }

  private toPngDataUrl(image: string | null | undefined): string | null {
    if (!image) {
      return null;
    }

    if (image.startsWith('data:image/png;base64,')) {
      return image;
    }

    return `data:image/png;base64,${image}`;
  }

  trackByTextElementKey(_index: number, key: TextElementKey): TextElementKey {
    return key;
  }

  getTextElement(key: TextElementKey): DigitalCardTextElement | undefined {
    return this.digitalCardConfig.elements[key];
  }

  getTextElementColor(key: TextElementKey): string {
    return this.getTextElement(key)?.color || '#111827';
  }

  getTextElementValue(key: TextElementKey): string | number | null | undefined {
    const digitalCard = this.digitalCard as any;

    const values: Record<TextElementKey, string | number | null | undefined> = {
      productName: digitalCard.productName,
      sku: digitalCard.sku,
      serialNumber: digitalCard.serialNumber,
      warrantyCode: digitalCard.warrantyCode,
      purchaseDate: digitalCard.purchaseDate,
      retailerName: digitalCard.retailerName
    };

    return values[key];
  }

  async generateQrCode(itemId: number): Promise<void> {
    const payload: DigitalCardRequest = {
      itemId
    };

    this.qrData = '';
    this.qrUrlIsMissing = false;
    this.isFetchingQrData = true;

    try {
      const response = await firstValueFrom(this.digitalCardsService.apiDigitalCardsQrPost(payload));

      const qrUrl = response.response;

      if (qrUrl) {
        this.qrData = qrUrl;
        return;
      }

      this.qrUrlIsMissing = true;
    } catch (error) {
      if (this.isNotFoundError(error)) {
        this.qrUrlIsMissing = true;
        return;
      }

      console.error('Error fetching digital card QR URL:', error);
    } finally {
      this.isFetchingQrData = false;
    }
  }

  copyToClipboard() {
    const url = this.qrData;

    if (!url) {
      console.log('Nothing to copy!');
      return;
    }

    navigator.clipboard.writeText(url).then(() => {
      const toastTitle = this.translate.instant('DIGITAL_CARD_SINGLE_VIEW.COPY_SUCCESS_TITLE');
      const toastMessage = this.translate.instant('DIGITAL_CARD_SINGLE_VIEW.COPY_SUCCESS_MESSAGE');
      this.toasterService.show('success', toastTitle, toastMessage);
    }).catch(err => {
      console.error('Failed to copy the QR URL to the clipboard:', err);
    });
  }

  printDigitalCard() {
    window.print();
  }

  emailDigitalCard() {
    this.displayEmailModalbox = true;
    this.emailForm.patchValue({
      email:this.digitalCard?.customerEmail
    });
  }

  onCancelEmailModal() {
    this.displayEmailModalbox = false;
    this.emailForm.reset();
  }

  async onSubmitEmailForm() {
    if (this.emailForm.invalid) return;

    const payload: DigitalCardEmailRequest = {
      itemId: this.itemId,
      customerEmail: this.emailForm.controls['email'].value,
    };

    try {
      await firstValueFrom(this.digitalCardsService.apiDigitalCardsSendPost(payload));
    }
    catch (error) {
      console.error('Error sending digital card email:', error);
    }

    this.displayEmailModalbox = false;
    this.emailForm.reset();
  }

  getReadonlyCardWidth(): number {
    return this.digitalCardConfig.card.width || 640;
  }

  getReadonlyCardHeight(): number {
    return this.digitalCardConfig.card.height || 360;
  }

  getReadonlyStageWidth(): number {
    return this.getReadonlyCardWidth() * this.readonlyCardScale;
  }

  getReadonlyStageHeight(): number {
    return this.getReadonlyCardHeight() * this.readonlyCardScale;
  }

  private setupReadonlyCardResizeObserver(): void {
    this.readonlyCardResizeObserver?.disconnect();

    const wrapper = this.digitalCardPreviewWrapper?.nativeElement;

    if (!wrapper || typeof ResizeObserver === 'undefined') {
      this.updateReadonlyCardScale();
      return;
    }

    this.readonlyCardResizeObserver = new ResizeObserver(() => {
      this.zone.run(() => this.updateReadonlyCardScale());
    });

    this.readonlyCardResizeObserver.observe(wrapper);
    this.updateReadonlyCardScale();
  }

  private updateReadonlyCardScale(): void {
    const wrapper = this.digitalCardPreviewWrapper?.nativeElement;

    if (!wrapper) {
      this.readonlyCardScale = 1;
      return;
    }

    this.readonlyCardScale = this.calculateScale(wrapper, this.getReadonlyCardWidth());
  }

  private calculateScale(wrapper: HTMLElement, cardWidth: number): number {
    const styles = window.getComputedStyle(wrapper);
    const horizontalPadding = parseFloat(styles.paddingLeft || '0') + parseFloat(styles.paddingRight || '0');
    const availableWidth = Math.max(0, wrapper.clientWidth - horizontalPadding);

    if (!availableWidth) {
      return 1;
    }

    return Math.min(1, availableWidth / (cardWidth || 1));
  }

  toggleSecurityPasscodeVisibility() {
    this.showSecurityPasscode = !this.showSecurityPasscode;
  }

  ngOnDestroy(): void {
    this.readonlyCardResizeObserver?.disconnect();
  }
}
